/* eslint-disable react/no-danger */
import { IconWithText, Modal, Tooltip } from '@pmi/dsm-react';
import { AlertQuestionIcon } from '@pmi/dsm-react/dist/components/icons/fa/16';
import { PlainIcon } from '@pmi/dsm-react/dist/components/icons/tt/mark/fc';
import {
  LeadershipIcon,
  StrategicIcon,
  TechnicalIcon,
} from '@pmi/dsm-react/dist/components/icons/tt/breakdown';
import {
  AlertSuccessOutlineIcon,
  DiscountOutlineIcon,
} from '@pmi/dsm-react/dist/components/icons';
import { useState } from 'react';
import { Product } from '../models';
import styles from '../productcard.module.scss';
import CategoryLabel from '../../../shared/components/CategoryLabel';
import {
  isPayByUserCartStatus,
  PAY_BY_USER_STATUS_MAP,
  ProductTypes,
} from '../../../shared/constants';

/**
 * Props for the DetailModal component.
 */
interface DetailModalProps {
  product: Product; // The product details to display in the modal
  onCancel: (report?: boolean) => void; // Callback for canceling the modal
  onContinue: () => void; // Callback for continuing the action
}

/**
 * DetailModal component.
 *
 * This component renders a modal to display detailed information about a product,
 * including its description, pricing, and other relevant details.
 *
 * @param {DetailModalProps} props - The props for the component.
 * @returns {JSX.Element} The rendered modal component.
 */
export default function DetailModal({
  product,
  onCancel,
  onContinue,
}: DetailModalProps) {
  const {
    name,
    fullDescription = '',
    type,
    level,
    waysOfWorkingPDUs,
    businessAcumenPDUs,
    powerSkillsPDUs,
    totalPDUs,
    callToAction,
  } = product;

  const buttonText = callToAction?.text;
  const [submitted, setSubmitted] = useState(false);

  /**
   * Formats the call-to-action text.
   * @param {string} text - The text to format.
   * @returns {string} The formatted text.
   */
  const ctaTextFormat = (text: string) => {
    if (!text) return '';
    if (text.toLowerCase() === "i'm ready to enroll") {
      return text.charAt(0).toUpperCase() + text.toLowerCase().slice(1);
    }
    return text;
  };

  // Define the modal action buttons
  const modalActionButtons: any[] = [
    {
      titleText: 'Cancel',
      variant: 'outline-secondary',
      onClick: onCancel,
    },
    {
      titleText: ctaTextFormat(buttonText),
      variant: 'primary',
      disabled:
        ctaTextFormat(buttonText)
          .toLowerCase()
          .trim()
          .includes(PAY_BY_USER_STATUS_MAP.inCart) || submitted,
      onClick: () => {
        setSubmitted(true);
        onContinue();
        setTimeout(() => {
          onCancel(false);
        }, 300);
      },
      className: 'btnCta',
      ref: (el: HTMLButtonElement | null) => {
        if (!el) return;
        const btn = el;
        btn.innerHTML = ctaTextFormat(buttonText);
      },
    },
  ].filter((x) => x.titleText);

  /**
   * Checks if the product is a "Pay By User" product.
   * @returns {boolean} True if the product is a "Pay By User" product, false otherwise.
   */
  const isPbuProduct = () =>
    product.isPayByUserProduct &&
    isPayByUserCartStatus(product.callToAction?.text) &&
    product.price > 0;

  /**
   * Renders the discount label for the product.
   * @returns {JSX.Element | null} The discount label or null if no discount is available.
   */
  const discountLabel = () => {
    let text = '';

    if (product.price < product.msrpPrice) {
      if (
        (product.isPmiMember || product.hasMembershipInCart) &&
        product.discountPercent <= 0
      ) {
        text = `Price includes Member discount`;
      } else if (
        (product.isPmiMember || product.hasMembershipInCart) &&
        product.discountPercent > 0
      ) {
        text = `Price includes Member + ${product.discountPercent}% company discount`;
      } else if (product.discountPercent > 0) {
        text = `Price includes ${product.discountPercent}% company discount`;
      }
    }

    if (text.length === 0) {
      return null;
    }

    return (
      <p className={`${styles.payByUserDiscount} mb-2`}>
        <IconWithText
          text={text}
          icon={<DiscountOutlineIcon />}
          className="d-none d-sm-flex"
        />
        <div className="d-flex d-sm-none align-items-start mb-3">
          <p>
            <AlertSuccessOutlineIcon
              fontSize="sm"
              className="d-inline-block float-start"
            />{' '}
            <span className="d-inline">{text}</span>
          </p>
        </div>
      </p>
    );
  };

  return (
    <Modal
      show
      customClassName={`d-flex flex-column ${styles.productCard}`}
      variant="input"
      onClose={() => onCancel()}
      buttons={modalActionButtons}
    >
      <h4
        className="d-inline-block mb-0"
        data-testid="product-name"
        dangerouslySetInnerHTML={{ __html: name }}
        ref={() => {
          const btn: HTMLButtonElement = document.querySelector(
            `.${styles.productCard} button.btnCta`,
          );
          if (btn) {
            btn.innerHTML = ctaTextFormat(buttonText);
            setTimeout(() => {
              btn.innerHTML = ctaTextFormat(buttonText);
            }, 1);
          }
        }}
      />
      <div className="mb-2">
        {!product?.bundleOptions.some((x) =>
          x.title
            ?.toLowerCase()
            .includes(ProductTypes.membership.toLowerCase()),
        ) &&
          product?.bundleProduct && (
            <CategoryLabel type="Bundle" className="mb-3 me-2 float-start" />
          )}
        <CategoryLabel type={type} className="mb-3 d-inline-flex" />
      </div>
      <div className="label mb-4" data-testid="msg2">
        {type === 'eLearning' && (
          <>
            {level && <span data-testid="course-level">Level: {level}</span>}
            {level && totalPDUs && ' | '}
            {totalPDUs && `PDUs: ${totalPDUs}`}
          </>
        )}
        {type === 'Certification' && (
          <span>Application, Exam and Certification included.</span>
        )}
        {type === ProductTypes.membership && (
          <span>Annual Membership good for 1 year.</span>
        )}
      </div>
      {type === 'eLearning' && (
        <>
          <div className="mb-2 mb-sm-4 d-flex align-items-center">
            <b className="pe-2">Talent Triangle PDUs</b>
            <Tooltip
              className={`${styles.tooltip} darkTooltip`}
              type="sm"
              placement="top"
              content={
                <>
                  <b>Talent Triangle PDUs</b>
                  <div>
                    As a certification holder you must earn a minimum number of
                    Professional Development Units (PDUs) in each of the skill
                    areas of the PMI Talent Triangle - Ways of Working (formerly
                    Technical), Power Skills (formerly Leadership), Business
                    Acumen (formerly Strategic).{' '}
                    <a
                      href="https://www.pmi.org/certifications/certification-resources/maintain/earn-pdus"
                      target="_blank"
                      rel="noreferrer"
                      className="body5-link-violet"
                    >
                      Learn more about PDUs
                    </a>
                    .
                  </div>
                </>
              }
            >
              <div className="d-flex">
                <AlertQuestionIcon fontSize="xs" />
                <span className="ps-1 text-link--xs">What is this?</span>
              </div>
            </Tooltip>
          </div>
          <div className="mb-2 mb-md-4 d-flex flex-row justify-content-around">
            <div className="d-flex flex-column align-items-center text-center">
              <TechnicalIcon
                color="original"
                fontSize="lg"
                className={`ps-0 pe-0 ${styles.technicalIcon}`}
              />
              <div className="text-link--sm pt-1 pb-2">Ways of Working</div>
              <div
                className="text-link--lg fw-bold"
                data-testid="ways-of-working-pdus"
              >
                {waysOfWorkingPDUs ?? 0}
              </div>
            </div>
            <div className="d-flex flex-column align-items-center text-center">
              <StrategicIcon
                color="original"
                fontSize="lg"
                className={`ps-0 pe-0 ${styles.strategicIcon}`}
              />
              <div className="text-link--sm pt-1 pb-2">Business Acumen</div>
              <div
                className="text-link--lg fw-bold"
                data-testid="business-acumen-pdus"
              >
                {businessAcumenPDUs ?? 0}
              </div>
            </div>
            <div className="d-flex flex-column align-items-center text-center">
              <LeadershipIcon
                color="original"
                fontSize="lg"
                className={`ps-0 pe-0 ${styles.leadershipIcon}`}
              />
              <div className="text-link--sm pt-1 pb-2">Power Skills</div>
              <div
                className="text-link--lg fw-bold"
                data-testid="power-scills-pdus"
              >
                {powerSkillsPDUs ?? 0}
              </div>
            </div>
            <div className="d-flex flex-column align-items-center text-center content-label">
              <div className={`${styles.scaledIcon}`}>
                <PlainIcon
                  color="original"
                  fontSize="lg"
                  className="ps-0 pe-0"
                />
              </div>
              <div className="text-link--sm pt-1 pb-2">Total</div>
              <div className="text-link--lg fw-bold">{totalPDUs ?? 0}</div>
            </div>
          </div>
        </>
      )}
      <div
        className={isPbuProduct() ? `${styles.descriptionPbu}` : ''}
        data-testid="description"
        dangerouslySetInnerHTML={{
          __html: `${fullDescription || product.shortDescription}${
            fullDescription?.endsWith('.') ? '' : '.'
          }`,
        }}
      />
      {isPbuProduct() && (
        <div className={`${styles.modalPriceBlock} mb-3`}>
          <div className="d-block mb-3">{discountLabel()}</div>
          <div>
            <h3 className={`${styles.payByUserPrice} mb-4`}>
              {product.currencySymbol}
              {product.price.toFixed(2)}
              {product.price < product.msrpPrice && product.msrpPrice > 0 && (
                <span style={{ textDecoration: 'line-through' }}>
                  {product.currencySymbol}
                  {product.msrpPrice.toFixed(2)}
                </span>
              )}
            </h3>
          </div>
        </div>
      )}
    </Modal>
  );
}
